import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import Dialog from "../../components/Dialog/Dialog";
import LanguageSelector from "../../components/LanguageSelector/LanguageSelector";
import FrontPageLogo from "../../components/Logo/Logo";
import { GiEntryDoor } from "react-icons/gi";
import { Redirect, useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import i18n, { t } from "i18next";
import QuestionHeader from "../../components/QuestionHeader/QuestionHeader";

import Feedback from "../../shared/api/FeedbackApi";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";

import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import CasinoIcon from "@mui/icons-material/Casino";
import Paper from "@mui/material/Paper";
import { GameContext } from "../../shared/GameContext";
import _ from "underscore";
import { Box, CircularProgress, Grid, Typography, useTheme } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LobbyApi from "../../shared/api/LobbyApi";
import Urls from "../../shared/api/Urls";
import { getRandomPlayerName } from "../../shared/helper/functions";
import useGameContext from "../../shared/useGameContext";
import { getQueueSocket, disconnectQueueSocket } from "../../shared/api/QueueSocket";
import { useAuth0 } from "@auth0/auth0-react";
import Deck from "../../shared/api/DeckApi";

export default function Start() {
  const history = useHistory();
  const [name, setName] = useState("");

  const [enteringCode, setEnteringCode] = useState(false);
  const [codeLetter1, setCodeLetter1] = useState("");
  const [codeLetter2, setCodeLetter2] = useState("");
  const [codeLetter3, setCodeLetter3] = useState("");
  const [codeLetter4, setCodeLetter4] = useState("");
  const [codeLetter5, setCodeLetter5] = useState("");
  const [justDeletedCharacter, setJustDeletedCharacter] = useState(true);
  const [invalidName, setInvalidName] = useState(false);
  const setLobbyKey = useGameContext().setLobbyKey;
  const nameInputEl = useRef<HTMLInputElement>(null);
  const inputEl1 = useRef<HTMLInputElement>(null);
  const inputEl2 = useRef<HTMLInputElement>(null);
  const inputEl3 = useRef<HTMLInputElement>(null);
  const inputEl4 = useRef<HTMLInputElement>(null);
  const inputEl5 = useRef<HTMLInputElement>(null);
  const [invalidLobbyKey, setInvalidLobbyKey] = useState(false);

  // Queue state
  const [inQueue, setInQueue] = useState(false);
  const [queuePosition, setQueuePosition] = useState(0);
  const [queueTotal, setQueueTotal] = useState(0);
  const [membershipCode, setMembershipCode] = useState("");
  const [membershipCodeInvalid, setMembershipCodeInvalid] = useState(false);
  const [queueTimedOut, setQueueTimedOut] = useState(false);

  // Shutdown state
  const [shutdownActive, setShutdownActive] = useState(false);
  const [shutdownMessage, setShutdownMessage] = useState("");
  const [shutdownEndsAt, setShutdownEndsAt] = useState(0);
  const [shutdownCountdown, setShutdownCountdown] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);

  const pollShutdown = useCallback(async () => {
    try {
      const resp = await fetch(Urls.SHUTDOWN_STATUS);
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.active) {
        setShutdownActive(true);
        setShutdownMessage(data.message || "");
        setShutdownEndsAt(data.endsAt || 0);
      } else {
        setShutdownActive(false);
        setFeedbackSent(false);
      }
    } catch {
      // ignore network errors
    }
  }, []);

  useEffect(() => {
    pollShutdown();
    const id = setInterval(pollShutdown, 30_000);
    return () => clearInterval(id);
  }, [pollShutdown]);

  // Countdown timer for shutdown
  useEffect(() => {
    if (!shutdownActive || !shutdownEndsAt) {
      setShutdownCountdown("");
      return;
    }
    function tick() {
      const remaining = Math.max(0, shutdownEndsAt - Date.now());
      if (remaining <= 0) {
        setShutdownCountdown("Now");
        return;
      }
      const mins = Math.floor(remaining / 60_000);
      const secs = Math.floor((remaining % 60_000) / 1000);
      setShutdownCountdown(`${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [shutdownActive, shutdownEndsAt]);

  async function sendShutdownFeedback() {
    if (!feedbackText.trim()) return;
    try {
      await fetch(Urls.SHUTDOWN_FEEDBACK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from: "User", text: feedbackText.trim() }),
      });
      setFeedbackSent(true);
      setFeedbackText("");
    } catch {
      // ignore
    }
  }

  // Auto-fill name from profile when authenticated
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  useEffect(() => {
    if (isAuthenticated && name === "") {
      (async () => {
        try {
          const token = await getAccessTokenSilently();
          const res = await Deck.getProfileName(token);
          if (res?.name) {
            setName(res.name);
          }
        } catch (_) {
          // fail silently — user can still type or use dice
        }
      })();
    }
  }, [isAuthenticated]);

  const handleCodeLetterChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setCodeLetter: (codeLetter: string) => void
  ) => {
    setCodeLetter(event.target.value.toUpperCase());
  };

  useEffect(() => {
    if (codeLetter1.length !== 0) {
      inputEl2.current?.focus();
    } else {
      setJustDeletedCharacter(true);
    }
  }, [codeLetter1]);

  useEffect(() => {
    if (codeLetter2.length === 0) {
      setJustDeletedCharacter(true);
    } else {
      inputEl3.current?.focus();
    }
  }, [codeLetter2]);

  useEffect(() => {
    if (codeLetter3.length === 0) {
      setJustDeletedCharacter(true);
    } else {
      inputEl4.current?.focus();
    }
  }, [codeLetter3]);

  useEffect(() => {
    if (codeLetter4.length === 0) {
      setJustDeletedCharacter(true);
    } else {
      inputEl5.current?.focus();
    }
  }, [codeLetter4]);

  useEffect(() => {
    if (codeLetter4.length === 0) {
      setJustDeletedCharacter(true);
    }
  }, [codeLetter5]);

  // Cleanup queue socket on unmount
  useEffect(() => {
    return () => {
      disconnectQueueSocket();
    };
  }, []);

  const [globalName, setGlobalName] = [
    useContext(GameContext).nameState,
    useContext(GameContext).setNameState,
  ];

  const DiceButton = () => {
    return (
      <IconButton
        onClick={() => {
          setName(getRandomPlayerName());

        }}
      >
        <CasinoIcon />
      </IconButton>
    );
  };

  const handleExitCodeInput = () => {
    setCodeLetter1("");
    setCodeLetter2("");
    setCodeLetter3("");
    setCodeLetter4("");
    setCodeLetter5("");
    setEnteringCode(false);
  };
  const checkIfPlayerNameValid = () => {
    if (name.trim().length === 0) {
      setInvalidName(false);
      nameInputEl.current?.focus();
      return false;
    } else {
      setGlobalName(name);
      return true;
    }
  };
  const handleStartJoin = () => {
    if (checkIfPlayerNameValid()) {
      setEnteringCode(true);
    }
  };

  const handleFinishJoin = async () => {
    let lobbyKey = codeLetter1 + codeLetter2 + codeLetter3 + codeLetter4 + codeLetter5;
    console.log("key: " + lobbyKey);
    if (lobbyKey.length === 5) {
      let canJoin = (await LobbyApi.checkLobbyKey(lobbyKey)).canJoin;
      console.log("canJoin: " + canJoin);
      if (canJoin) {
        setLobbyKey(lobbyKey);
        history.push("/lobby");
      } else {
        setInvalidLobbyKey(true);
        setTimeout(() => {
          if (invalidLobbyKey) {
            setInvalidLobbyKey(false);
          }
        }, 1200);
      }
    }
  };

  const connectToQueue = (playerName: string) => {
    const queueSocket = getQueueSocket();

    queueSocket.on("queuePosition", ({ position, total }: { position: number; total: number }) => {
      setQueuePosition(position);
      setQueueTotal(total);
    });

    queueSocket.on("queueReady", ({ lobbyKey }: { lobbyKey: string }) => {
      disconnectQueueSocket();
      setInQueue(false);
      setLobbyKey(lobbyKey);
      history.push("/lobby");
    });

    queueSocket.on("queueTimeout", () => {
      disconnectQueueSocket();
      setInQueue(false);
      setQueueTimedOut(true);
    });

    queueSocket.on("membershipCodeResult", ({ valid }: { valid: boolean }) => {
      if (!valid) {
        setMembershipCodeInvalid(true);
      }
    });

    // On reconnect, re-join the queue
    queueSocket.on("connect", () => {
      queueSocket.emit("joinQueue", { playerName });
    });

    queueSocket.connect();
  };

  // Whether to show the shutdown panel (set when user tries to create a lobby during shutdown)
  const [showShutdownPanel, setShowShutdownPanel] = useState(false);

  const handleCreateNewLobby = async () => {
    if (checkIfPlayerNameValid()) {
      // If we already know shutdown is active, show the panel immediately
      if (shutdownActive) {
        setShowShutdownPanel(true);
        return;
      }
      const result = await LobbyApi.newLobby();
      if (!result) return;
      if (result.shutdown) {
        // Shutdown activated since page load
        await pollShutdown();
        setShowShutdownPanel(true);
        return;
      }
      if (result.lobbyKey && !result.queued) {
        setLobbyKey(result.lobbyKey);
        history.push("/lobby");
      } else if (result.queued) {
        setInQueue(true);
        setQueueTimedOut(false);
        connectToQueue(name);
      }
    }
  };

  const handleLeaveQueue = () => {
    const queueSocket = getQueueSocket();
    queueSocket.emit("leaveQueue");
    disconnectQueueSocket();
    setInQueue(false);
    setMembershipCode("");
    setMembershipCodeInvalid(false);
  };

  const handleSubmitMembershipCode = () => {
    if (membershipCode.trim().length === 0) return;
    setMembershipCodeInvalid(false);
    const queueSocket = getQueueSocket();
    queueSocket.emit("submitMembershipCode", { code: membershipCode.trim() });
  };

  const handleCodeLetterChangeOnKeyUp = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    previousLetterRef: React.RefObject<HTMLInputElement>
  ) => {
    if (e.key == "Backspace" && !justDeletedCharacter) {
      previousLetterRef.current?.focus();
      setJustDeletedCharacter(false);
    } else if (e.key == "Enter") {
      console.log("executing finishJoin ");
      handleFinishJoin();
    }
  };

  const handleCodeLetterChangeOnKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.key == "Backspace") {
      setJustDeletedCharacter(false);
    }
  };

  return (
    <Stack
      alignItems="center"
      sx={{ height: "100vh" }}
      spacing={2}
    >
      <Box sx={{ display: { xs: "none", md: "block" }, position: "fixed", bottom: 16, right: 16, zIndex: 100 }}>
        <LanguageSelector />
      </Box>
      <Stack
        direction="column"
        spacing={2}
        justifyContent="flex-start"
        paddingTop={10}
        alignItems="center"
      >
        <FrontPageLogo></FrontPageLogo>
        {showShutdownPanel ? (
          <Stack
            sx={{ width: { md: "40vw", xs: "80vw" } }}
            alignItems="center"
            spacing={3}
          >
            <Paper sx={{ padding: 4, width: "100%", textAlign: "center" }}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
                <WarningAmberIcon color="warning" fontSize="large" />
                <Typography variant="h4">Scheduled Maintenance</Typography>
              </Box>
              <Typography variant="h2" sx={{ fontFamily: "monospace", my: 2 }}>
                {shutdownCountdown}
              </Typography>
              {shutdownMessage && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {shutdownMessage}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                New lobbies are temporarily disabled. You can still join an existing lobby.
              </Typography>
              {!feedbackSent ? (
                <Box display="flex" gap={1} mt={2}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Leave a message for the admin..."
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendShutdownFeedback()}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={sendShutdownFeedback}
                    disabled={!feedbackText.trim()}
                    sx={{ flexShrink: 0 }}
                  >
                    Send
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                  Message sent!
                </Typography>
              )}
            </Paper>
            <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
              <Button
                variant="contained"
                sx={{ flex: 1, height: "4em", borderRadius: 3, textTransform: "none", fontSize: "1.1rem" }}
                onClick={() => setShowShutdownPanel(false)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                sx={{ flex: 1, height: "4em", borderRadius: 3, textTransform: "none", fontSize: "1.1rem" }}
                onClick={() => {
                  setShowShutdownPanel(false);
                  handleStartJoin();
                }}
              >
                {t("join_lobby")}
              </Button>
            </Stack>
          </Stack>
        ) : inQueue ? (
          <Stack
            sx={{ width: { md: "40vw", xs: "80vw" } }}
            alignItems="center"
            spacing={3}
          >
            <Paper sx={{ padding: 4, width: "100%", textAlign: "center" }}>
              <Typography variant="h4" gutterBottom>
                {t("queue_title")}
              </Typography>
              <Typography
                variant="h2"
                sx={{ fontWeight: "bold", my: 2 }}
              >
                {queuePosition}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {t("queue_position_of", { position: queuePosition, total: queueTotal })}
              </Typography>
              <CircularProgress sx={{ my: 2 }} />

              <Stack spacing={2} sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("queue_membership_prompt")}
                </Typography>
                <TextField
                  value={membershipCode}
                  onChange={(e) => {
                    setMembershipCode(e.target.value);
                    setMembershipCodeInvalid(false);
                  }}
                  placeholder={t("queue_code_placeholder")}
                  error={membershipCodeInvalid}
                  helperText={membershipCodeInvalid ? t("queue_invalid_code") : ""}
                  size="small"
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSubmitMembershipCode();
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSubmitMembershipCode}
                  disabled={membershipCode.trim().length === 0}
                  sx={{ borderRadius: 3, textTransform: "none", fontSize: "1.1rem" }}
                >
                  {t("queue_submit_code")}
                </Button>
                <Button variant="contained" onClick={handleLeaveQueue} sx={{ borderRadius: 3, textTransform: "none", fontSize: "1.1rem" }}>
                  {t("queue_leave")}
                </Button>
              </Stack>
            </Paper>
          </Stack>
        ) : enteringCode ? (
          <Stack spacing={3}>
            <Stack direction="row" spacing={1}>
              <TextField
                value={codeLetter1}
                onChange={(e) => {
                  handleCodeLetterChange(e, setCodeLetter1);
                }}
                sx={{ width: "3em", height: "3em" }}
                inputProps={{ ref: inputEl1, style: { textAlign: "center" }, maxLength: 1 }}
              />
              <TextField
                value={codeLetter2}
                onChange={(e) => {
                  handleCodeLetterChange(e, setCodeLetter2);
                }}
                sx={{ width: "3em", height: "3em" }}
                inputProps={{
                  ref: inputEl2,
                  style: { textAlign: "center" },
                  maxLength: 1,
                  onKeyDown: (e) => handleCodeLetterChangeOnKeyDown(e),
                  onKeyUp: (e) => handleCodeLetterChangeOnKeyUp(e, inputEl1),
                }}
              />
              <TextField
                value={codeLetter3}
                onChange={(e) => {
                  handleCodeLetterChange(e, setCodeLetter3);
                }}
                sx={{ width: "3em", height: "3em" }}
                inputProps={{
                  ref: inputEl3,
                  style: { textAlign: "center" },
                  maxLength: 1,
                  onKeyDown: (e) => handleCodeLetterChangeOnKeyDown(e),
                  onKeyUp: (e) => handleCodeLetterChangeOnKeyUp(e, inputEl2),
                }}
              />
              <TextField
                value={codeLetter4}
                onChange={(e) => {
                  handleCodeLetterChange(e, setCodeLetter4);
                }}
                sx={{ width: "3em", height: "3em" }}
                inputProps={{
                  ref: inputEl4,
                  style: { textAlign: "center" },
                  maxLength: 1,
                  onKeyDown: (e) => handleCodeLetterChangeOnKeyDown(e),
                  onKeyUp: (e) => handleCodeLetterChangeOnKeyUp(e, inputEl3),
                }}
              />
              <TextField
                value={codeLetter5}
                onChange={(e) => {
                  handleCodeLetterChange(e, setCodeLetter5);
                }}
                sx={{ width: "3em", height: "3em" }}
                inputProps={{
                  ref: inputEl5,
                  style: { textAlign: "center" },
                  maxLength: 1,
                  onKeyDown: (e) => handleCodeLetterChangeOnKeyDown(e),
                  onKeyUp: (e) => handleCodeLetterChangeOnKeyUp(e, inputEl4),
                }}
              />
            </Stack>
            <Stack spacing={2}>
              <Button
                color={invalidLobbyKey ? "error" : "primary"}
                variant="contained"
                disabled={
                  !(
                    (codeLetter1 + codeLetter2 + codeLetter3 + codeLetter4 + codeLetter5).length ===
                    5
                  )
                }
                onClick={handleFinishJoin}
                sx={{ borderRadius: 3, textTransform: "none", fontSize: "1.1rem", py: 1.2 }}
              >
                JOIN
              </Button>

              <Button variant="contained" onClick={handleExitCodeInput} sx={{ borderRadius: 3, textTransform: "none", fontSize: "1.1rem", py: 1.2 }}>
                {t("join_exit")}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack sx={{ width: { md: "40vw", xs: "80vw" } }} alignItems="center" spacing={2}>
            {queueTimedOut && (
              <Paper sx={{ padding: 2, width: "100%", textAlign: "center", bgcolor: "warning.light" }}>
                <Typography variant="body2">{t("queue_timeout")}</Typography>
              </Paper>
            )}
            <Paper sx={{ width: "100%" }}>
              <TextField
                color={invalidName ? "error" : "primary"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                variant="outlined"
                label="Name"
                InputLabelProps={{ style: { fontSize: 22 } }}
                InputProps={{ endAdornment: <DiceButton />, style: { fontSize: 22 } }}
                inputProps={{ ref: nameInputEl, maxLength: 18 }}
              />
            </Paper>
            <Stack
              direction={{ xs: "column", md: "row" }}
              sx={{ minHeight: { md: "4em", xs: "" }, width: "100%" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Button
                sx={{ width: { xs: "100%", md: "40%" }, height: { xs: "4em", md: "100%" }, borderRadius: 3, textTransform: "none", fontSize: "1.1rem" }}
                onClick={handleCreateNewLobby}
                variant="contained"
              >
                {t("create_new_lobby")}
              </Button>

              <Button
                sx={{ width: { xs: "100%", md: "40%" }, height: { xs: "4em", md: "100%" }, borderRadius: 3, textTransform: "none", fontSize: "1.1rem" }}
                onClick={handleStartJoin}
                variant="contained"
              >
                {t("join_lobby")}
              </Button>
              <Button
                variant="contained"
                sx={{ width: { xs: "100%", md: "20%" }, height: { xs: "4em", md: "100%" }, borderRadius: 3, textTransform: "none", fontSize: "1.1rem" }}
                onClick={() => history.push("")}
              >
                Exit
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
